/**
 * TanStack Query hooks for /api/v1/availability-rules/.
 *
 * Gated by the `registration_rules` feature flag.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import type { PaginatedResponse } from '@/shared/types/api'

export type AvailabilityScope =
  | 'warranty_template'
  | 'product'
  | 'product_template'
  | 'warranty_template_option'
  | 'warranty_filter'
  | 'entity_registration'

export interface WarrantyAttributeFilter {
  code_name: string
  value: unknown
}

export interface AvailabilityQueryFilters {
  /**
   * How this rule's narrowed set composes with other matched
   * `warranty_filter` rules:
   * - `'include'` (default): contributes to the OR-union of allowed
   *   templates. Used for additive grants like "≥4 components unlock
   *   all tiers" and the unconditional "Standard floor".
   * - `'cap'`: contributes to an AND-intersection ceiling applied
   *   *after* the include union. Used for contractor-tier visibility
   *   so a Preferred Contractor never sees Platinum even when an
   *   include rule would lift them above their tier.
   */
  mode?: 'include' | 'cap'
  deny_all?: boolean
  category_ids?: string[]
  warranty_types?: string[]
  template_ids?: string[]
  attribute_filters?: WarrantyAttributeFilter[]
  /**
   * Geographic zones (uuids) the products must be available in. Only meaningful
   * for `scope: 'product'` rules - joined through `ProductAvailability`.
   * Gated by the `geographic_availability` feature flag on the backend.
   */
  geo_zone_ids?: string[]
}

export interface AvailabilityQueryFilterMetaOption {
  value: string
  label: string
}

export interface AvailabilityQueryFilterMetaReference {
  uuid: string
  label: string
}

export interface AvailabilityQueryFilterMeta {
  mode?: 'include' | 'cap'
  deny_all?: { value: true; label: string }[]
  category_ids?: AvailabilityQueryFilterMetaReference[]
  warranty_types?: AvailabilityQueryFilterMetaOption[]
  template_ids?: AvailabilityQueryFilterMetaReference[]
  geo_zone_ids?: AvailabilityQueryFilterMetaReference[]
}

export interface AvailabilityRule {
  uuid: string
  name: string
  description: string | null
  conditions: PredicateNode | null
  severity: Severity
  message: string
  is_active: boolean
  priority: number
  scope: AvailabilityScope
  scope_id: string | null
  effect_overrides: Record<string, unknown>
  query_filters: AvailabilityQueryFilters
  query_filter_meta: AvailabilityQueryFilterMeta
  effective_from: string | null
  effective_until: string | null
  created_at: string
  updated_at: string
}

export interface AvailabilityRuleInput {
  name: string
  description?: string | null
  conditions: PredicateNode | null
  severity: Severity
  message?: string | null
  is_active?: boolean
  priority?: number
  scope: AvailabilityScope
  scope_id?: string | null
  effect_overrides?: Record<string, unknown>
  query_filters?: AvailabilityQueryFilters
  effective_from?: string | null
  effective_until?: string | null
}

const KEY = ['availability-rules'] as const

export function useAvailabilityRules(params?: {
  scope?: string
  scope_id?: string
  is_active?: 'true' | 'false'
  severity?: Severity
  q?: string
  search?: string
  ordering?: string
}) {
  const enabled = useFeatureFlag(FLAGS.REGISTRATION_RULES)
  return useQuery<AvailabilityRule[]>({
    queryKey: [...KEY, params],
    // Some callers (e.g. product-detail Guarantees card) derive the full set
    // of dispatched warranty templates from the response and would silently
    // drop templates whose rules land on page 2+. Request the StandardPagination
    // ceiling (1000) so a single page covers any realistic per-product rule set.
    queryFn: () =>
      api
        .get<PaginatedResponse<AvailabilityRule>>('/v1/availability-rules/', {
          params: { page_size: 1000, ...params },
        })
        .then((r) => r.data.results),
    staleTime: 30_000,
    enabled,
  })
}

export function useAvailabilityRulesPage(params?: {
  scope?: string
  scope_id?: string
  is_active?: 'true' | 'false'
  severity?: Severity
  q?: string
  search?: string
  page?: number
  page_size?: number
  ordering?: string
}) {
  const enabled = useFeatureFlag(FLAGS.REGISTRATION_RULES)
  return useQuery<PaginatedResponse<AvailabilityRule>>({
    queryKey: [...KEY, 'page', params],
    queryFn: () =>
      api
        .get<PaginatedResponse<AvailabilityRule>>('/v1/availability-rules/', {
          params,
        })
        .then((r) => r.data),
    staleTime: 30_000,
    enabled,
  })
}

// ---------------------------------------------------------------------------
// Policy groups (server-side grouping by policy_code)
// ---------------------------------------------------------------------------

export interface PolicyGroupTemplateSummary {
  uuid: string
  name: string
  warranty_type: string
}

export interface PolicyGroupProductSummary {
  uuid: string
  cached_short_name: string
}

export interface PolicyGroupSummary {
  policy_code: string
  start_date: string | null
  end_date: string | null
  rule_count: number
  warranty_templates: PolicyGroupTemplateSummary[]
  warranty_types: string[]
  mapped_products: PolicyGroupProductSummary[]
  rules: AvailabilityRule[]
}

export function usePolicyGroupsPage(params?: {
  page?: number
  page_size?: number
  q?: string
  search?: string
}) {
  const enabled = useFeatureFlag(FLAGS.REGISTRATION_RULES)
  return useQuery<PaginatedResponse<PolicyGroupSummary>>({
    queryKey: [...KEY, 'policies', params],
    queryFn: () =>
      api
        .get<PaginatedResponse<PolicyGroupSummary>>(
          '/v1/availability-rules/policies/',
          { params },
        )
        .then((r) => r.data),
    staleTime: 30_000,
    enabled,
  })
}

export function useCreateAvailabilityRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: AvailabilityRuleInput) =>
      api.post('/v1/availability-rules/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateAvailabilityRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      ...data
    }: { uuid: string } & Partial<AvailabilityRuleInput>) =>
      api.patch(`/v1/availability-rules/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteAvailabilityRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/v1/availability-rules/${uuid}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useBulkDeleteAvailabilityRules() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuids: string[]) =>
      api
        .post('/v1/availability-rules/bulk-delete/', { uuids })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

// ---------------------------------------------------------------------------
// Availability evaluation (wizard preview of warranty option gating)
// ---------------------------------------------------------------------------

export interface AvailabilityEvaluateOption {
  template_uuid: string
  option_uuid?: string | null
  product_uuid?: string | null
  product_template_uuid?: string | null
}

export interface AvailabilityEvaluateRequest {
  context: {
    fields?: Record<string, unknown>
    entities?: Record<string, Record<string, unknown>>
    aggregates?: Record<string, unknown>
    roles?: string[]
    permissions?: string[]
  }
  options: AvailabilityEvaluateOption[]
}

export interface AvailabilityResult {
  template_uuid: string
  option_uuid: string | null
  available: boolean
  unavailable_reason: string | null
  warnings: string[]
}

export interface AvailabilityEvaluateResponse {
  results: AvailabilityResult[]
}

/** Pre-render check - call once the wizard knows the host context. */
export async function evaluateAvailabilityRules(
  body: AvailabilityEvaluateRequest,
): Promise<AvailabilityEvaluateResponse> {
  const r = await api.post('/v1/availability-rules/evaluate/', body)
  return r.data as AvailabilityEvaluateResponse
}

/**
 * Build a stable lookup key for an `AvailabilityResult`. Use the same
 * key on the wizard side to look up a template / option pair.
 */
export function availabilityKey(
  templateUuid: string,
  optionUuid: string | null,
): string {
  return `${templateUuid}::${optionUuid ?? ''}`
}
